import React, { useCallback, useEffect, useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const relative = (iso) => {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h ago`;
  return new Date(iso).toLocaleDateString();
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [load]);

  const onOpenChange = async (isOpen) => {
    setOpen(isOpen);
    if (isOpen && unread > 0) {
      try {
        await api.post("/notifications/read-all");
        setUnread(0);
        setItems((current) => current.map((n) => ({ ...n, read: true })));
      } catch {
        // keep the badge if marking fails; next open retries
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          className="btn-ghost !py-2 !px-3 relative"
          aria-label={unread ? `${unread} unread notices` : "Notices"}
          data-testid="notification-bell"
        >
          <Bell size={15} />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold flex items-center justify-center"
              style={{ background: "#E8871E", color: "#fff" }}
              data-testid="notification-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] !rounded-none p-0" style={{ borderColor: "var(--border)" }} data-testid="notification-panel">
        <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <span className="eyebrow">Notices</span>
          {Boolean(items.length) && (
            <span className="text-xs flex items-center gap-1" style={{ color: "var(--muted)" }}>
              <CheckCheck size={12} /> All read
            </span>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto divide-y" style={{ borderColor: "var(--border)" }}>
          {items.map((notice) => (
            <button
              key={notice.id}
              onClick={() => { setOpen(false); if (notice.link) navigate(notice.link); }}
              className="w-full text-left px-4 py-3 hover:bg-[#F3F3F1] transition-colors"
              data-testid={`notification-${notice.id}`}
            >
              <div className="text-sm font-medium" style={{ color: "var(--ink)" }}>{notice.title}</div>
              <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>{notice.body}</div>
              <div className="text-[10px] mt-1.5 uppercase tracking-wider" style={{ color: "var(--muted)" }}>
                {relative(notice.created_at)}
              </div>
            </button>
          ))}
          {!items.length && (
            <div className="px-4 py-10 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="notifications-empty">
              No notices yet. Invoice decisions and refund credits show up here.
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
